import { adminPath, type AdminClient, type ApiPage, type ApiResponse } from '../core/index.js'
import type { DashboardSummary, FacilityAddress, FacilityConfig, HolidayEvent, TierDistribution } from './types.js'

/** One row of `GET /admin/clients/{clientId}/facilities`. */
export interface AdminFacility {
  facilityId: string
  clientId: string
  name: string
  createdAt: string
  /** IANA zone the facility's days are reckoned in. Absent on a facility registered before it existed, which reads as UTC. */
  timezone?: string
  /** Set only by the operator; the admin reads them and never changes them. */
  address?: FacilityAddress
  phone?: string
  /** Optional only because an API deployed before this count existed omits it. */
  workerCount?: number
}

/** The facilities module's admin calls: the records, and the dashboard reads of a facility of any client. */
export interface FacilitiesAdminApi {
  listFacilities(clientId: string): Promise<AdminFacility[]>
  /** One page of a client's facilities in name order, optionally only those whose name starts with `q`. The total comes with the first page. */
  listFacilitiesPage(clientId: string, request: { limit: number; cursor: string | null; q?: string }): Promise<ApiPage<AdminFacility>>
  /** Changes a facility's name or time zone through the admin path. Never the address: only the operator sets that. */
  updateFacility(clientId: string, facilityId: string, input: { name?: string; timezone?: string }): Promise<AdminFacility>
  /** The dashboard figures, tier spread, configuration and holiday events of a facility of any client, read-only; each 404s until the facility is configured. */
  facilityDashboardSummary(clientId: string, facilityId: string): Promise<DashboardSummary>
  facilityTierDistribution(clientId: string, facilityId: string): Promise<TierDistribution>
  facilityConfig(clientId: string, facilityId: string): Promise<FacilityConfig>
  facilityHolidayEvents(clientId: string, facilityId: string): Promise<HolidayEvent[]>
}

export function createFacilitiesAdminApi(client: AdminClient): FacilitiesAdminApi {
  return {
    listFacilities: (clientId) => client.call('GET', `${adminPath.client(clientId)}/facilities`),
    listFacilitiesPage: async (clientId, { limit, cursor, q }) => {
      const query = new URLSearchParams({ limit: String(limit), ...(q ? { q } : {}), ...(cursor ? { cursor } : {}) })
      const envelope = await client.call<ApiResponse<AdminFacility[]>>('GET', `${adminPath.client(clientId)}/facilities?${query.toString()}`, undefined, true)
      return { items: envelope.data ?? [], nextCursor: envelope.meta?.nextCursor ?? null, total: envelope.meta?.total ?? null }
    },
    updateFacility: (clientId, facilityId, input) => client.call('PATCH', adminPath.facility(clientId, facilityId), input),
    facilityDashboardSummary: (clientId, facilityId) => client.call('GET', `${adminPath.facility(clientId, facilityId)}/dashboard/summary`),
    facilityTierDistribution: (clientId, facilityId) => client.call('GET', `${adminPath.facility(clientId, facilityId)}/dashboard/tier-distribution`),
    facilityConfig: (clientId, facilityId) => client.call('GET', `${adminPath.facility(clientId, facilityId)}/config`),
    facilityHolidayEvents: (clientId, facilityId) => client.call('GET', `${adminPath.facility(clientId, facilityId)}/holiday-events`),
  }
}
